import { REWARDS, REWARD_IDS, ROUTE_NODE_COUNTS, SAVE_KEY_V1, SAVE_KEY_V2, WORLD_CUP_ROUTE_NODE_COUNTS } from "./constants";
import { getStartingClubs } from "./clubs";
import {
  calculateEncounterWinProbability,
  canAcquireReward,
  clamp,
  effectiveRating,
  encounterDifficulty,
  enforceStageMapStructure,
  generateStageMap,
} from "./engine";
import type { CareerHistoryEntry, FinaleKind, GameStateV2, Inventory, MapNode, Player, RelicRewardId, RewardId } from "./types";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const numberOr = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const stringOr = (value: unknown, fallback: string): string =>
  typeof value === "string" ? value : fallback;

function rewardIdFromLegacy(value: unknown): RewardId | null {
  if (!isRecord(value)) return null;
  if (typeof value.id === "string" && REWARD_IDS.includes(value.id as RewardId)) return value.id as RewardId;
  const name = typeof value.name === "string" ? value.name : "";
  return REWARD_IDS.find((id) => REWARDS[id].name === name) ?? null;
}

function migrateInventory(value: unknown): Inventory {
  const result: Inventory = {};
  if (Array.isArray(value)) {
    value.forEach((legacyReward) => {
      const rewardId = rewardIdFromLegacy(legacyReward);
      if (!rewardId) return;
      const current = result[rewardId]?.count ?? 0;
      result[rewardId] = { rewardId, kind: REWARDS[rewardId].kind, count: current + 1 };
    });
    return result;
  }
  if (!isRecord(value)) return result;
  REWARD_IDS.forEach((rewardId) => {
    const stack = value[rewardId];
    if (!isRecord(stack)) return;
    const count = Math.min(REWARDS[rewardId].stackLimit ?? Number.POSITIVE_INFINITY, Math.max(0, Math.floor(numberOr(stack.count, 0))));
    if (count) result[rewardId] = { rewardId, kind: REWARDS[rewardId].kind, count };
  });
  return result;
}

function migratePlayer(value: unknown): Player {
  const player = isRecord(value) ? value : {};
  return {
    name: stringOr(player.name, "Álex Cruz"),
    number: stringOr(player.number, "10"),
    country: stringOr(player.country, "España"),
    position: player.position === "DC" ? "DC" : "MCO",
  };
}

const LEGACY_CLUBS: Readonly<Record<string, string>> = {
  "Atlético Mirador": "Athletic Bilbao",
  "Sporting del Puerto": "Getafe",
  "Unión Celeste": "Celta Vigo",
  "Real Montaña": "Barcelona",
  "Estrella del Norte": "Atlético Madrid",
  "Getafe CF": "Getafe",
  "CA Osasuna": "Osasuna",
  "FC Barcelona": "Barcelona",
  "RCD Espanyol de Barcelona": "Espanyol",
  "RC Deportivo": "Deportivo de A Coruña",
};

function migrateClub(value: unknown): string {
  const club = stringOr(value, getStartingClubs("España")[0]?.name ?? "Athletic Bilbao");
  return LEGACY_CLUBS[club] ?? club;
}

function migrateMap(value: unknown): MapNode[][] {
  if (!Array.isArray(value)) return [];
  return value.map((column) => Array.isArray(column)
    ? column.filter(isRecord).map((node, row) => ({
      id: stringOr(node.id, `legacy-${row}`),
      type: node.type === "rest" || node.type === "event" || node.type === "scout" || node.type === "derby" ? node.type : "match",
      row: numberOr(node.row, row),
      column: numberOr(node.column, 0),
    }))
    : []);
}

function isCurrentMapShape(map: MapNode[][], season?: number, finaleKind?: FinaleKind | null): boolean {
  const counts = season !== undefined && season >= 5 && finaleKind === "world-cup" ? WORLD_CUP_ROUTE_NODE_COUNTS : ROUTE_NODE_COUNTS;
  return map.length === counts.length
    && map.every((column, columnIndex) => column.length === counts[columnIndex]);
}

function migrateCareerHistory(value: unknown): CareerHistoryEntry[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).map((entry) => {
    const stage = Math.max(1, Math.min(5, Math.floor(numberOr(entry.stage, 1))));
    return {
      stage,
      stageName: stringOr(entry.stageName, "Etapa"),
      team: stringOr(entry.team, "Club desconocido"),
      appearances: Math.max(0, Math.floor(numberOr(entry.appearances, 0))),
      minutesPlayed: Math.max(0, Math.floor(numberOr(entry.minutesPlayed, 0))),
      goals: Math.max(0, Math.floor(numberOr(entry.goals, 0))),
      assists: Math.max(0, Math.floor(numberOr(entry.assists, 0))),
      ovrStart: clamp(Math.floor(numberOr(entry.ovrStart, 60)), 0, 99),
      ovrEnd: clamp(Math.floor(numberOr(entry.ovrEnd, 60)), 0, 99),
      titlesWon: Math.max(0, Math.floor(numberOr(entry.titlesWon, 0))),
      outcome: entry.outcome === "completed" ? "completed" : "eliminated",
      summary: stringOr(entry.summary, "Etapa registrada."),
    };
  });
}

export function migrateV1(value: unknown): GameStateV2 | null {
  if (!isRecord(value)) return null;
  const season = Math.max(1, Math.floor(numberOr(value.season, 1)));
  const finaleKind = season >= 5 ? "world-cup" as const : null;
  const migratedMap = migrateMap(value.map);
  const preservesRoute = isCurrentMapShape(migratedMap, season, finaleKind);
  const boss = isRecord(value.boss) ? {
    round: Math.max(0, Math.floor(numberOr(value.boss.round, 0))),
    wins: Math.max(0, Math.floor(numberOr(value.boss.wins, 0))),
    scores: Array.isArray(value.boss.scores)
      ? value.boss.scores.filter((score): score is "win" | "loss" => score === "win" || score === "loss")
      : [],
  } : null;
  return {
    saveVersion: 2,
    player: migratePlayer(value.player),
    season,
    finaleKind,
    week: preservesRoute ? Math.max(0, Math.floor(numberOr(value.week, 0))) : 0,
    ovr: clamp(Math.floor(numberOr(value.ovr, 60)), 0, 99),
    physical: clamp(numberOr(value.physical, 100)),
    morale: clamp(numberOr(value.morale, 70)),
    clubReputation: clamp(numberOr(value.clubReputation, 45)),
    appearances: Math.max(0, Math.floor(numberOr(value.appearances, 0))),
    minutesPlayed: Math.max(0, Math.floor(numberOr(value.minutesPlayed, 0))),
    goals: Math.max(0, Math.floor(numberOr(value.goals, 0))),
    assists: Math.max(0, Math.floor(numberOr(value.assists, 0))),
    titles: Math.max(0, Math.floor(numberOr(value.titles, 0))),
    wins: Math.max(0, Math.floor(numberOr(value.wins, 0))),
    derbyWins: Math.max(0, Math.floor(numberOr(value.derbyWins, 0))),
    bestWinStreak: Math.max(0, Math.floor(numberOr(value.bestWinStreak, 0))),
    seasonsCompleted: Math.max(0, Math.floor(numberOr(value.seasonsCompleted, 0))),
    careerHistory: migrateCareerHistory(value.careerHistory),
    stageBaseline: {
      appearances: Math.max(0, Math.floor(numberOr(isRecord(value.stageBaseline) ? value.stageBaseline.appearances : value.appearances, 0))),
      minutesPlayed: Math.max(0, Math.floor(numberOr(isRecord(value.stageBaseline) ? value.stageBaseline.minutesPlayed : value.minutesPlayed, 0))),
      goals: Math.max(0, Math.floor(numberOr(isRecord(value.stageBaseline) ? value.stageBaseline.goals : value.goals, 0))),
      assists: Math.max(0, Math.floor(numberOr(isRecord(value.stageBaseline) ? value.stageBaseline.assists : value.assists, 0))),
      ovr: clamp(Math.floor(numberOr(isRecord(value.stageBaseline) ? value.stageBaseline.ovr : value.ovr, 60)), 0, 99),
      titles: Math.max(0, Math.floor(numberOr(isRecord(value.stageBaseline) ? value.stageBaseline.titles : value.titles, 0))),
    },
    leagueOpponentsPlayed: Array.isArray(value.leagueOpponentsPlayed) ? value.leagueOpponentsPlayed.filter((entry): entry is string => typeof entry === "string") : [],
    leagueMovements: [],
    oneClubEligible: value.oneClubEligible !== false,
    club: migrateClub(value.club),
    inventory: migrateInventory(value.inventory),
    log: Array.isArray(value.log) ? value.log.filter((entry): entry is string => typeof entry === "string").slice(0, 12) : [],
    map: preservesRoute ? enforceStageMapStructure(migratedMap, season, finaleKind) : generateStageMap(season, Math.random, finaleKind ?? "world-cup"),
    path: preservesRoute && Array.isArray(value.path) ? value.path.filter((entry): entry is string => typeof entry === "string") : [],
    activeNodeId: null,
    boss: preservesRoute ? boss : null,
    finished: value.finished === true,
    selectedCardId: null,
    pendingMatch: null,
    pendingScoutOffer: [],
    winStreak: 0,
    relicUses: {},
  };
}

export function saveGame(storage: StorageLike, state: GameStateV2): void {
  storage.setItem(SAVE_KEY_V2, JSON.stringify(state));
}

function normalizeV2(state: GameStateV2): GameStateV2 {
  const { xp: _legacyXp, destination: _legacyDestination, ...currentState } = state as GameStateV2 & { xp?: unknown; destination?: unknown };
  const normalizedClub = migrateClub(currentState.club);
  const careerHistory = migrateCareerHistory(currentState.careerHistory);
  const rawBaseline: UnknownRecord = isRecord(currentState.stageBaseline) ? currentState.stageBaseline : {};
  const stageBaseline = {
    appearances: Math.max(0, Math.floor(numberOr(rawBaseline.appearances, numberOr(currentState.appearances, 0)))),
    minutesPlayed: Math.max(0, Math.floor(numberOr(rawBaseline.minutesPlayed, numberOr(currentState.minutesPlayed, 0)))),
    goals: Math.max(0, Math.floor(numberOr(rawBaseline.goals, currentState.goals))),
    assists: Math.max(0, Math.floor(numberOr(rawBaseline.assists, currentState.assists))),
    ovr: clamp(Math.floor(numberOr(rawBaseline.ovr, currentState.ovr)), 0, 99),
    titles: Math.max(0, Math.floor(numberOr(rawBaseline.titles, currentState.titles))),
  };
  const finaleKind = currentState.season >= 5
    ? currentState.finaleKind === "treble" ? "treble" as const : "world-cup" as const
    : null;
  const preservesRoute = isCurrentMapShape(currentState.map, currentState.season, finaleKind);
  const normalizedMap = preservesRoute
    ? enforceStageMapStructure(currentState.map, currentState.season, finaleKind)
    : generateStageMap(currentState.season, Math.random, finaleKind ?? "world-cup");
  const leagueOpponentsPlayed = Array.isArray(currentState.leagueOpponentsPlayed) ? currentState.leagueOpponentsPlayed.filter((entry): entry is string => typeof entry === "string") : [];
  const leagueMovements = Array.isArray(currentState.leagueMovements)
    ? currentState.leagueMovements.filter((movement) => isRecord(movement) && typeof movement.promotedClub === "string" && typeof movement.relegatedClub === "string" && typeof movement.country === "string")
      .map((movement) => ({ promotedClub: String(movement.promotedClub), relegatedClub: String(movement.relegatedClub), country: String(movement.country), afterStage: Math.max(1, Math.floor(numberOr(movement.afterStage, 3))) }))
    : [];
  const clubReputation = clamp(numberOr(currentState.clubReputation, 45));
  const normalizedOvr = clamp(Math.floor(numberOr(currentState.ovr, 60)), 0, 99);
  const pendingMatch = currentState.pendingMatch ? {
    ...currentState.pendingMatch,
    winProbability: calculateEncounterWinProbability(
      effectiveRating(
        { ...currentState, ovr: normalizedOvr },
        numberOr(currentState.pendingMatch.ratingModifier, 0)
          + numberOr(currentState.pendingMatch.relicRatingBonus, 0)
          + numberOr(currentState.pendingMatch.matchupModifier, 0),
        currentState.pendingMatch.ignorePhysicalPenalty === true,
      ),
      encounterDifficulty(
        currentState.pendingMatch.encounterKind,
        currentState.season,
        currentState.pendingMatch.encounterKind === "boss" ? currentState.boss?.round ?? 0 : currentState.week,
      ),
    ),
  } : null;
  const relicUses = isRecord(currentState.relicUses)
    ? Object.fromEntries(Object.entries(currentState.relicUses).filter(([id, stage]) => REWARD_IDS.includes(id as RewardId) && typeof stage === "number")) as Partial<Record<RelicRewardId, number>>
    : {};
  const pendingScoutOffer = Array.isArray(currentState.pendingScoutOffer)
    ? currentState.pendingScoutOffer.filter((id): id is RewardId => typeof id === "string" && REWARD_IDS.includes(id as RewardId) && canAcquireReward(migrateInventory(currentState.inventory), id as RewardId)).slice(0, 3)
    : [];
  return {
    ...currentState,
    ovr: normalizedOvr,
    finaleKind,
    club: normalizedClub,
    map: normalizedMap,
    week: preservesRoute ? Math.min(currentState.week, normalizedMap.length) : 0,
    path: preservesRoute ? currentState.path.slice(0, normalizedMap.length) : [],
    activeNodeId: preservesRoute ? currentState.activeNodeId : null,
    boss: preservesRoute ? currentState.boss : null,
    careerHistory,
    stageBaseline,
    leagueOpponentsPlayed,
    leagueMovements,
    clubReputation,
    appearances: Math.max(0, Math.floor(numberOr(currentState.appearances, 0))),
    minutesPlayed: Math.max(0, Math.floor(numberOr(currentState.minutesPlayed, 0))),
    inventory: migrateInventory(currentState.inventory),
    pendingMatch: preservesRoute ? pendingMatch : null,
    pendingScoutOffer: preservesRoute ? pendingScoutOffer : [],
    wins: Math.max(0, Math.floor(numberOr(currentState.wins, 0))),
    derbyWins: Math.max(0, Math.floor(numberOr(currentState.derbyWins, 0))),
    bestWinStreak: Math.max(0, Math.floor(numberOr(currentState.bestWinStreak, currentState.winStreak))),
    winStreak: Math.max(0, Math.floor(numberOr(currentState.winStreak, 0))),
    relicUses,
  };
}

export function parseGameState(value: unknown): GameStateV2 | null {
  if (!isRecord(value)) return null;
  if (value.saveVersion === 2) return normalizeV2(value as unknown as GameStateV2);
  return migrateV1(value);
}

export function loadGame(storage: StorageLike): GameStateV2 | null {
  const v2 = storage.getItem(SAVE_KEY_V2);
  if (v2) {
    try {
      const normalized = parseGameState(JSON.parse(v2) as unknown);
      if (normalized) {
        saveGame(storage, normalized);
        return normalized;
      }
    } catch { /* Fall through to the recoverable v1 save. */ }
  }
  const v1 = storage.getItem(SAVE_KEY_V1);
  if (!v1) return null;
  try {
    const migrated = migrateV1(JSON.parse(v1) as unknown);
    if (migrated) saveGame(storage, migrated);
    return migrated;
  } catch {
    return null;
  }
}

export function clearGame(storage: StorageLike): void {
  storage.removeItem(SAVE_KEY_V2);
  storage.removeItem(SAVE_KEY_V1);
}
